// @flow

import React, { PureComponent } from 'react';
import { createPaginationContainer, graphql } from 'react-relay';
import debounce from 'lodash.debounce';
import type { RouterHistory } from 'react-router';
import { BrowserUtils } from 'shared/utils';
import LoadingMore from 'shared/components/common/LoadingMore';
import EmptySearchResults from 'shared/components/common/EmptySearchResults';
import BackordersTable from './BackordersTable';

type Props = {
  history: RouterHistory,
  filtersDirty: boolean,
  onCreateNew: () => void,
  backorders: Object,
  relay: {
    environment: Object,
    hasMore: () => boolean,
    isLoading: () => boolean,
    loadMore: (pageSize: number, callback: Function) => void,
  },
};

class PaginatedBackordersContainer extends PureComponent<Props> {
  constructor(props: Props) {
    super(props);
    this.onScrollEndReached = debounce(this.onScrollEndReached, 100);
  }

  componentDidMount = () => {
    document.addEventListener('scroll', this.onScrollEndReached);
  };

  componentWillUnmount = () => {
    document.removeEventListener('scroll', this.onScrollEndReached);
  };

  onScrollEndReached = () => {
    const { relay } = this.props;
    if (BrowserUtils.scrollEndReached(200)) {
      if (relay.hasMore()) {
        relay.loadMore(30);
      }
    }
  };

  render() {
    const {
      history,
      onCreateNew,
      backorders,
      relay,
      filtersDirty,
    } = this.props;

    if (
      (!backorders.backorders.edges || !backorders.backorders.edges.length) &&
      filtersDirty
    ) {
      return <EmptySearchResults />;
    }

    return (
      <>
        <BackordersTable
          history={history}
          onCreateNew={onCreateNew}
          backorderEdges={backorders.backorders.edges}
        />
        {relay.hasMore() && relay.isLoading() ? <LoadingMore /> : null}
      </>
    );
  }
}

export default createPaginationContainer(
  PaginatedBackordersContainer,
  {
    backorders: graphql`
      fragment PaginatedBackordersContainer_backorders on Query
        @argumentDefinitions(
          count: { type: "Int", defaultValue: 30 }
          cursor: { type: "String" }
          search: { type: "String" }
          sortBy: { type: "BackorderSort" }
          statuses: { type: "[BackorderStatus!]" }
          customerIds: { type: "[ID!]" }
          orderIds: { type: "[ID!]" }
        ) {
        backorders(
          first: $count
          after: $cursor
          search: $search
          sortBy: $sortBy
          statuses: $statuses
          customerIds: $customerIds
          orderIds: $orderIds
        ) @connection(key: "PaginatedBackordersContainer_backorders", filters: []) {
          edges {
            cursor
            ...BackordersTable_backorderEdges
          }
        }
      }
    `,
  },
  {
    query: graphql`
      query PaginatedBackordersContainerQuery(
        $count: Int!
        $cursor: String
        $search: String
        $sortBy: BackorderSort
        $statuses: [BackorderStatus!]
        $customerIds: [ID!]
        $orderIds: [ID!]
      ) {
        ...PaginatedBackordersContainer_backorders
          @arguments(
            count: $count
            cursor: $cursor
            search: $search
            sortBy: $sortBy
            statuses: $statuses
            customerIds: $customerIds
            orderIds: $orderIds
          )
      }
    `,
    getFragmentVariables(prevVars, totalCount) {
      return {
        ...prevVars,
        count: totalCount,
      };
    },
    getVariables(props, { count, cursor }, fragmentVariables) {
      return { ...fragmentVariables, count, cursor };
    },
  },
);
